import React, { useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { resizeCanvas, resizeCanvasToDisplaySize } from './util';

const LINE_COLOR = "rgba(160, 99, 163, 0.37)";
const LINE_WIDTH = 2;

const FrequencyResponse = ({ audioEnv, globalMode, mapToFreqAndAmp, mapToCanvas, ...rest }) => {
    const canvasRef = useRef(null);
    const animationFrameIdRef = useRef(null);
    const question = useSelector((state) => state.filters.question);



    useEffect(() => {
        // Helper: calculate frequencies across the entire canvas width
        const calculateFrequencies = (canvas, context) => {
            const frequencies = new Float32Array(canvas.width);
            for (let x = 0; x < frequencies.length; x++) {
                const { frequency } = mapToFreqAndAmp(context, x, 0);
                frequencies[x] = frequency;
            }
            return frequencies;
        };

        // Helper: draw a single frequency-response curve (no clearing inside this function!)
        const drawSingleFrequencyResponse = (canvas, context, processors, strokeColor = LINE_COLOR) => {
            const frequencies = calculateFrequencies(canvas, context);
            const magResponse = new Float32Array(frequencies.length);
            const phaseResponse = new Float32Array(frequencies.length);
            const frequencyResponse = new Float32Array(frequencies.length).fill(1);

            const contextHeight = canvas.height;
            // dB range for Y-axis
            const minDb = -12;
            const maxDb = 12;

            // Combine magnitude responses of all processors
            processors.forEach((p) => {
                const biquadFilter = p.getNode?.();
                if (!biquadFilter || p.getBypass()) return;
                biquadFilter.getFrequencyResponse(frequencies, magResponse, phaseResponse);
                for (let i = 0; i < frequencies.length; i++) {
                    frequencyResponse[i] *= magResponse[i];
                }
            });

            context.beginPath();
            const startX = mapToCanvas(context, frequencies[0], 0).x;
            const startY =
                contextHeight -
                ((16 * Math.log10(frequencyResponse[0]) - minDb) / (maxDb - minDb)) * contextHeight;

            context.moveTo(startX, startY);

            let lastValue = startY;

            for (let i = 0; i < frequencies.length; i++) {
                const gain = frequencyResponse[i];
                const db = 16 * Math.log10(gain);
                const y =
                    contextHeight - ((db - minDb) / (maxDb - minDb)) * contextHeight;

                const { x } = mapToCanvas(context, frequencies[i], 0);
                context.lineTo(x, y);
                lastValue = y;
            }

            // Extend to the right edge
            context.lineTo(canvas.width, lastValue);

            context.strokeStyle = strokeColor;
            context.lineWidth = LINE_WIDTH;
            context.stroke();
        };

        // Helper: dynamic drawing (for user-setting mode)
        const animateUserResponse = (canvas, context, processors) => {
            context.clearRect(0, 0, canvas.width, canvas.height);
            drawSingleFrequencyResponse(canvas, context, processors, LINE_COLOR);

            // Schedule next frame
            animationFrameIdRef.current = requestAnimationFrame(() =>
                animateUserResponse(canvas, context, processors)
            );
        };
        
        const canvas = canvasRef.current;
        if (!canvas) return;
        const context = canvas.getContext('2d');

        // Resize
        resizeCanvas(canvas);
        resizeCanvasToDisplaySize(canvas);

        // If we have no audioEnv or no processors, do nothing
        if (!audioEnv) return;

        // Clear any previous animation
        if (animationFrameIdRef.current) {
            cancelAnimationFrame(animationFrameIdRef.current);
        }
        context.clearRect(0, 0, canvas.width, canvas.height);

        if (globalMode === 'user-setting') {
            // 1. Get the user's current processors
            const userProcessors = audioEnv.getProcessors();
            // 2. Start animation for the user curve
            animateUserResponse(canvas, context, userProcessors);
        } else if (globalMode === 'check') {
            // In CHECK mode, draw two static lines

            // 1) "User" line as it currently is
            const userProcessors = audioEnv.getProcessors();
            drawSingleFrequencyResponse(canvas, context, userProcessors, LINE_COLOR);

            // 2) "Question" line (if question is available)
            //    You might need a fresh copy of the environment, or you can reuse the same
            //    if it doesn’t interfere with the userProcessors. 
            //    For example:
            const questionEnv = audioEnv;
            questionEnv.updateAllProcessors(question);
            const questionProcessors = questionEnv.getProcessors();

            // Draw in a different color, e.g. blue
            drawSingleFrequencyResponse(canvas, context, questionProcessors, "rgba(98, 179, 209, 0.584)");
        }

        // Cleanup
        return () => {
            if (animationFrameIdRef.current) {
                cancelAnimationFrame(animationFrameIdRef.current);
            }
            context.clearRect(0, 0, canvas.width, canvas.height);
        };
    }, [audioEnv, globalMode, question, mapToCanvas, mapToFreqAndAmp]);

    return <canvas className="fr-canvas eq-canvas" ref={canvasRef} {...rest} />;
};

export default FrequencyResponse;
